import { Injectable, TemplateRef } from '@angular/core';
import { UserRemoteControllerService } from 'core/api';
import { IUserDto } from 'core/dto';
import { ControlDataSource, DynamicFormService, IControl } from 'core/dynamic-form';
import { filterAllTruthyInTuple } from 'core/rx-js';
import { SplugConfigService } from 'core/splug';
import { BehaviorSubject, Subject, combineLatest, debounceTime, distinctUntilChanged, filter, map, of, startWith, switchMap, take, takeUntil, tap } from 'rxjs';
import { IActor } from '../../../../../dto-map/i-actor';
import { RemoteActorService } from '../../../../../remotes/remote-actor.service';
import { RemoteSplugParametersConfigurationService } from '../../../../../remotes/remote-splug-parameters-configuration.service';
import { ActivityItemService } from '../activity-item.service';
import { ActorSplugInfoDialogService } from '../actor-splug-info-dialog.service';

type ActorFormModel = Pick<IActor, 'email' | 'firstName' | 'lastName' | 'mobile'>

@Injectable()
export class ActorService {
  private readonly _destroy = new Subject<void>()

  readonly controls: IControl[] = [
    {
      dataField: 'email',
      label: 'e-mail',
      type: 'autocomplete',
      options: {
        dataSource: new ControlDataSource<IUserDto, string>({
          keyField: 'email',
          load: (loadOptions) => this._userRemoteControllerService.getMany({
            filter: loadOptions.searchValue,
            pageNo: loadOptions.skip / loadOptions.take,
            pageSize: loadOptions.take,
            sortBy: 'firstName'
          }),
          byKey: (email) => of(null)
        }),
        useRemotes: true,
        pageSize: 20,
        valueIsDisplay: true,
        valueExpr: 'email',
        displayExpr: 'email'
      },
      validationRules: [
        { type: 'required' },
        { type: 'email' }
      ]
    } as IControl<'autocomplete'>,
    {
      dataField: 'firstName',
      label: 'ss-entities.actor.first-name',
      type: 'text',
      validationRules: [
        { type: 'required' }
      ]
    } as IControl<'text'>,
    {
      dataField: 'lastName',
      label: 'ss-entities.actor.last-name',
      type: 'text',
      validationRules: [
        { type: 'required', }
      ]
    } as IControl<'text'>,
    {
      dataField: 'mobile',
      label: 'ss-entities.actor.mobile',
      type: 'text',
    } as IControl<'text'>,
  ];

  readonly suffixTemplateSubject = new BehaviorSubject<TemplateRef<any>>(null);

  readonly mainFormGroupSubject = this._dynamicFormService.mainFormGroupSubject
  get mainFormGroup() {
    return this.mainFormGroupSubject.value;
  }

  readonly actorSubject = this._remoteActorService.localItemStore.stateSubject;
  get actor() {
    return this.actorSubject.value;
  }
  readonly actorIdSubject = this._remoteActorService.keyValueSubject;

  readonly selectedUserSubject = new BehaviorSubject<IUserDto>(undefined);
  get selectedUser() {
    return this.selectedUserSubject.value;
  }

  get activityId() {
    return this._activityItemService.activityId;
  }


  readonly configDisabledObs = combineLatest([
    this.actorSubject,
    this.mainFormGroupSubject
  ])
    .pipe(
      switchMap(([actor, mainFormGroup]) => {
        if (actor) {
          return of(false)
        }

        if (!mainFormGroup) {
          return of(true);
        }

        return mainFormGroup.statusChanges.pipe(startWith(mainFormGroup.status), map(status => status !== 'VALID'))
      }),
    )

  get activity() {
    return this._activityItemService.activitySubject.value
  }
  get splugDefinitions() {
    return this._splugConfigService.splugDefinitions;
  }




  constructor(
    private readonly _remoteActorService: RemoteActorService,
    private readonly _dynamicFormService: DynamicFormService,
    private readonly _userRemoteControllerService: UserRemoteControllerService,
    private readonly _activityItemService: ActivityItemService,
    private readonly _actorSplugInfoDialogService: ActorSplugInfoDialogService,
    private readonly _remoteSplugParametersConfigurationService: RemoteSplugParametersConfigurationService,
    private readonly _splugConfigService: SplugConfigService,

  ) {

    this._dynamicFormService.controls = this.controls;

    this.mainFormGroupSubject.pipe(
      filter(Boolean),
      switchMap((formGroup) =>
        formGroup.statusChanges.pipe(startWith('INVALID' as const)).pipe(
          filter((status) => status === 'VALID'),
          switchMap(() => formGroup.valueChanges.pipe(startWith(formGroup.value))),

        )
      ),
      debounceTime(2 * 1000),
      filter((formData) =>
        !this.actor
        || formData.email !== this.actor.email
        || formData.firstName !== this.actor.firstName
        || formData.lastName !== this.actor.lastName
        || formData.mobile !== this.actor.mobile
      ),
      takeUntil(this._destroy)
    )
      .subscribe((formData) => {

        const keyValue = this._remoteActorService.keyValue;
        const userId = this.selectedUser?.id;

        if (keyValue) {

          this._remoteActorService.update({ ...formData, activityId: this.activityId, userId }, 'updateLocal').pipe(take(1)).subscribe();
          return;
        }


        this.insertActor().pipe(take(1)).subscribe();
      })

    this._activityItemService.activitySubject
      .pipe(
        filter(Boolean),
        map(({ actor }) => actor),
        filter(Boolean),
        distinctUntilChanged(),
        takeUntil(this._destroy)
      )
      .subscribe((actor) => {
        this.actorIdSubject.next(actor.id);
        this.actorSubject.next(actor);
      })

    this.selectedUserSubject
      .pipe(
        map(val => val as IUserDto),
        filter(Boolean),
        takeUntil(this._destroy)
      )
      .subscribe(user => {
        const mainFormGroup = this.mainFormGroupSubject.value;

        const mappedUser = {
          firstName: user.firstName,
          lastName: user.lastName,
          mobile: user.mobile
        } as const;

        ([
          'firstName',
          'lastName',
          'mobile'
        ] as const).forEach(dataField => {

          const ctrl = mainFormGroup.get(dataField);
          ctrl.setValue(user ? mappedUser[dataField] : '')
        })

      })


    combineLatest([
      this.actorSubject,
      this.mainFormGroupSubject.pipe(filter(Boolean), take(1))
    ])
      .pipe(
        filterAllTruthyInTuple(),
        takeUntil(this._destroy)
      )
      .subscribe(([actor, mainFormGroup]) => {
        const { email, firstName, lastName, mobile } = actor;

        mainFormGroup.setValue({
          email,
          firstName,
          lastName,
          mobile
        })
      })

  }

  openConfig() {


    if (!this.actor && this.mainFormGroup.valid) {

      this.insertActor()
        .pipe(
          take(1),
          switchMap(() => this.actorSubject.pipe(filter(Boolean))),
          take(1)
        )
        .subscribe((actor) => {
          this._actorSplugInfoDialogService.open(actor);
        })
      return;
    }

    this._actorSplugInfoDialogService.open({ ...this.actor, ...this.mainFormGroup.value });
  }


  insertActor() {
    const { email, firstName, lastName, mobile, userId } = this.mainFormGroup.value;

    return this._remoteActorService.insert({ email, firstName, lastName, mobile, userId, activityId: this.activityId })
      .pipe(
        tap(actorId => {

          this._remoteSplugParametersConfigurationService.loadByActivity(this.activityId)
            .pipe(take(1)).subscribe()
        })
      )
  }


  ngOnDestroy() {
    this._destroy.next()
    this._destroy.complete()
  }


}
